import { useRouter } from "next/router";
import { type FunctionComponent, useEffect, useState } from "react";
import { ErrorBoundary } from "react-error-boundary";
import type { PreloadedQuery } from "react-relay";
import { useFragment, usePreloadedQuery } from "react-relay";

import { ClientSideSuspense } from "shared/components/common/client-side-suspense";
import { parseQueryString } from "shared/lib/utils";

import { latestContentsQueryFragment } from "scmp-app/components/home/latest";
import { ExperimentAPINames, useActiveExperiment } from "scmp-app/lib/optimizely";
import { homePageQueryNode } from "scmp-app/pages/home/index.page";
import type { homePageQuery } from "scmp-app/queries/__generated__/homePageQuery.graphql";
import type { latestContentsQuery$key } from "scmp-app/queries/__generated__/latestContentsQuery.graphql";

import { Container, SkeletonBox, SkeletonWrapper } from "./styles";
import { TrendingNavBarLatestSwiper } from "./trending-nav-bar-latest-swiper";
import { TrendingNavBarSwiper } from "./trending-nav-bar-swiper";

export type Props = {
  className?: string;
  queryReference: PreloadedQuery<homePageQuery>;
};

export const TrendingNavBar: FunctionComponent<Props> = ({ className, queryReference }) => {
  const { query } = useRouter();
  const edition = parseQueryString(query.edition) ?? "int";
  const { experiment } = useActiveExperiment(ExperimentAPINames.SubNavTest);
  const subNavVariant = experiment?.variation?.name ?? "0";

  const [isReadyToShow, setIsReadyToShow] = useState(false);

  useEffect(() => {
    if (experiment !== undefined) {
      setIsReadyToShow(true);
      return;
    }

    const fallbackTimer = setTimeout(() => {
      setIsReadyToShow(true);
    }, 3000);

    return () => clearTimeout(fallbackTimer);
  }, [experiment]);

  const homePageData = usePreloadedQuery(homePageQueryNode, queryReference);
  const latestData = useFragment<latestContentsQuery$key>(
    latestContentsQueryFragment,
    homePageData,
  );
  const latestItems = latestData.contents.edges.slice(0, 4);

  if (!isReadyToShow) {
    return (
      <SkeletonWrapper>
        {Array.from({ length: 4 }).map((_, index) => (
          <SkeletonBox $isWide={index % 2 !== 0} key={index} />
        ))}
      </SkeletonWrapper>
    );
  }

  return (
    <Container $variant={subNavVariant} className={className}>
      <ErrorBoundary fallback={<></>}>
        <>
          {subNavVariant === "0" && edition && (
            <ClientSideSuspense>
              <TrendingNavBarSwiper edition={edition} />
            </ClientSideSuspense>
          )}

          {subNavVariant === "1" && (
            <TrendingNavBarLatestSwiper edition={edition} latestItems={latestItems} />
          )}
        </>
      </ErrorBoundary>
    </Container>
  );
};

TrendingNavBar.displayName = "TrendingNavBar";
